import { useEffect, useRef, useState } from "react";
import type { Rpc } from "../lib/rpc";

// Mirrors JOB_CONFIGS in electron-app/main.js. Order matters for display.
const JOBS: Array<{ id: string; label: string; logFile: string; needsLlava?: boolean }> = [
  { id: "0",  label: "0  LLaVA Service",      logFile: "0_llava_service.log",   needsLlava: false },
  { id: "1",  label: "1  Ingest Media",       logFile: "1_ingest_media.log" },
  { id: "3",  label: "3  Describe Media",     logFile: "3_describe_media.log",  needsLlava: true },
  { id: "5",  label: "5  Ingest RedGifs",     logFile: "5_ingest_redgifs.log",  needsLlava: true },
  { id: "6",  label: "6  Ingest Reddit",      logFile: "6_ingest_reddit.log" },
  { id: "7",  label: "7  Suggest Subreddits", logFile: "7_suggest_subreddits.log" },
  { id: "8",  label: "8  Monitor Email",      logFile: "8_monitor_email.log" },
];

interface JobState { running: boolean }

export function JobsPage({ rpc, electronConnected }: { rpc: Rpc; electronConnected: boolean }) {
  const [states, setStates] = useState<Record<string, JobState>>({});
  const [openLog, setOpenLog] = useState<string | null>(null);
  const [logText, setLogText] = useState<string>("");
  const [busy, setBusy] = useState<string | null>(null);
  const logTimerRef = useRef<number | null>(null);

  // Poll states every 3s while page is mounted.
  useEffect(() => {
    if (!electronConnected) return;
    let cancelled = false;
    async function tick() {
      try {
        const s = await rpc.call<Record<string, JobState>>("get-batch-job-states");
        if (!cancelled) setStates(s);
      } catch { /* swallow */ }
    }
    tick();
    const handle = window.setInterval(tick, 3000);
    return () => { cancelled = true; window.clearInterval(handle); };
  }, [rpc, electronConnected]);

  // Auto-refresh the open log every 2s.
  useEffect(() => {
    if (!openLog) return;
    if (logTimerRef.current !== null) window.clearInterval(logTimerRef.current);
    let cancelled = false;
    async function fetchLog() {
      try {
        const text = await rpc.call<string>("get-job-log", JOBS.find((j) => j.id === openLog)?.logFile);
        if (!cancelled) setLogText(text);
      } catch (e) {
        if (!cancelled) setLogText(`(error reading log: ${(e as Error).message})`);
      }
    }
    fetchLog();
    logTimerRef.current = window.setInterval(fetchLog, 2000);
    return () => {
      cancelled = true;
      if (logTimerRef.current !== null) window.clearInterval(logTimerRef.current);
    };
  }, [openLog, rpc]);

  async function start(id: string) {
    setBusy(`start:${id}`);
    try {
      await rpc.call("start-batch-job", id);
      const s = await rpc.call<Record<string, JobState>>("get-batch-job-states");
      setStates(s);
    } catch (e) { console.warn("[jobs] start failed", e); }
    finally { setBusy(null); }
  }
  async function stop(id: string) {
    setBusy(`stop:${id}`);
    try {
      await rpc.call("stop-batch-job", id);
      const s = await rpc.call<Record<string, JobState>>("get-batch-job-states");
      setStates(s);
    } catch (e) { console.warn("[jobs] stop failed", e); }
    finally { setBusy(null); }
  }

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <h2>⚙️ Batch Jobs</h2>
        <p className="hint">Pipeline stages running on the worker PC. Logs auto-refresh every 2s.</p>
      </header>

      <div className="jobs-list">
        {JOBS.map((j) => {
          const st = states[j.id];
          const running = !!st?.running;
          return (
            <div key={j.id} className={`job-card ${running ? "running" : ""}`}>
              <div className="job-row-top">
                <span className="job-name">{j.label}</span>
                <span className={`job-state ${running ? "ok" : "off"}`}>
                  {running ? "● running" : "○ stopped"}
                </span>
                <div className="job-actions">
                  {running ? (
                    <button onClick={() => stop(j.id)} disabled={busy !== null || !electronConnected}>
                      {busy === `stop:${j.id}` ? "…" : "Stop"}
                    </button>
                  ) : (
                    <button onClick={() => start(j.id)} disabled={busy !== null || !electronConnected}>
                      {busy === `start:${j.id}` ? "…" : "Start"}
                    </button>
                  )}
                  <button onClick={() => setOpenLog(openLog === j.id ? null : j.id)}>
                    {openLog === j.id ? "Hide log" : "Tail log"}
                  </button>
                </div>
              </div>
              {openLog === j.id && (
                <pre className="job-log">{logText || "(loading…)"}</pre>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
